import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

const Container = styled.div`
  margin: 32px 0;
`;

const Label = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#64748B' : '#94A3B8')};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
`;

const DemoArea = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.02)' : '#FFFFFF'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#E2E8F0'};
  border-radius: 12px;
  padding: ${(props) => (props.compact ? '24px' : '48px')};
  display: flex;
  align-items: ${(props) => props.align || 'center'};
  justify-content: ${(props) => props.justify || 'center'};
  gap: ${(props) => props.gap || '16px'};
  flex-wrap: ${(props) => (props.wrap ? 'wrap' : 'nowrap')};
  flex-direction: ${(props) => props.direction || 'row'};
  min-height: ${(props) => (props.minHeight ? props.minHeight : 'auto')};

  @media (max-width: 768px) {
    padding: 24px;
    flex-direction: ${(props) =>
      props.direction === 'row' ? 'column' : props.direction};
  }
`;

const Description = styled.p`
  font-size: 14px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  line-height: 1.6;
  margin: 12px 0 0 0;
`;

/**
 * ComponentDemo - A container for displaying component examples
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - The component(s) to demonstrate
 * @param {string} [props.label] - Optional label above the demo
 * @param {string} [props.description] - Optional description below the demo
 * @param {boolean} [props.compact] - Use smaller padding
 * @param {string} [props.align] - CSS align-items value
 * @param {string} [props.justify] - CSS justify-content value
 * @param {string} [props.gap] - Space between items
 * @param {boolean} [props.wrap] - Allow items to wrap
 * @param {string} [props.direction] - Flex direction (row, column)
 * @param {string} [props.minHeight] - Minimum height of demo area
 */
const ComponentDemo = ({
  children,
  label,
  description,
  compact = false,
  align,
  justify,
  gap,
  wrap = false,
  direction = 'row',
  minHeight,
}) => {
  return (
    <Container>
      {label && <Label>{label}</Label>}
      <DemoArea
        compact={compact}
        align={align}
        justify={justify}
        gap={gap}
        wrap={wrap}
        direction={direction}
        minHeight={minHeight}
      >
        {children}
      </DemoArea>
      {description && <Description>{description}</Description>}
    </Container>
  );
};

ComponentDemo.propTypes = {
  children: PropTypes.node.isRequired,
  label: PropTypes.string,
  description: PropTypes.string,
  compact: PropTypes.bool,
  align: PropTypes.string,
  justify: PropTypes.string,
  gap: PropTypes.string,
  wrap: PropTypes.bool,
  direction: PropTypes.string,
  minHeight: PropTypes.string,
};

export default ComponentDemo;